import { Color3, Mesh, MeshBuilder, type Scene, StandardMaterial, type InstancedMesh } from "@babylonjs/core";
import type { WorldConfig } from "../world/config";
import { AMBER_LEAF, AMBER_SHARE, LEAF_COLORS } from "./atmosphere";
import { CHUNK } from "./terrainMesh";

/** mulberry32: small, fast, deterministic. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Jittered grid with rejection: deterministic Poisson-like scatter in chunk-local coordinates. */
export function scatterPoints(cx: number, cz: number, density: number, seed: number, minDist: number): { x: number; z: number; r: number }[] {
  const n = Math.round(density * CHUNK * CHUNK);
  if (n <= 0) return [];
  const rand = rng((seed * 73856093) ^ (cx * 19349663) ^ (cz * 83492791));
  const cells = Math.ceil(Math.sqrt(n * 1.5));
  const cell = CHUNK / cells;
  const out: { x: number; z: number; r: number }[] = [];
  for (let i = 0; i < cells && out.length < n; i++) {
    for (let j = 0; j < cells && out.length < n; j++) {
      const x = (i + rand()) * cell, z = (j + rand()) * cell, r = rand();
      if (x >= CHUNK || z >= CHUNK) continue;
      if (out.every((p) => Math.hypot(p.x - x, p.z - z) >= minDist)) out.push({ x, z, r });
    }
  }
  return out;
}

/** One source mesh per leaf colour: two greens and amber. */
type TreeSet = [Mesh, Mesh, Mesh];

/** Which leaf colour a tree gets, from its scatter roll. Amber takes AMBER_SHARE of the low rolls. */
export function leafIndex(r: number): 0 | 1 | 2 {
  const a = r * 7919 % 1; // decorrelate from the species split, which also uses r
  return a < AMBER_SHARE ? 2 : a < AMBER_SHARE + (1 - AMBER_SHARE) / 2 ? 0 : 1;
}

export class SceneryManager {
  private readonly treeA: TreeSet;
  private readonly treeB: TreeSet;
  private readonly rock: Mesh;
  private readonly instances = new Map<string, InstancedMesh[]>();
  private readonly exclusion: (x: number, z: number) => boolean;

  constructor(scene: Scene, private readonly cfg: WorldConfig, private readonly height: (x: number, z: number) => number) {
    const leafMat = (name: string, hex: string): StandardMaterial => {
      const m = new StandardMaterial(name, scene);
      m.diffuseColor = Color3.FromHexString(hex);
      m.specularColor = Color3.Black();
      return m;
    };
    const leaves = [leafMat("leaf0", LEAF_COLORS[0]), leafMat("leaf1", LEAF_COLORS[1]), leafMat("leafAmber", AMBER_LEAF)];
    const bark = new StandardMaterial("bark", scene);
    bark.diffuseColor = new Color3(0.4, 0.28, 0.18);
    bark.specularColor = Color3.Black();
    const stone = new StandardMaterial("stone", scene);
    stone.diffuseColor = new Color3(0.5, 0.5, 0.52);
    stone.specularColor = Color3.Black();

    const mkTree = (name: string, h: number, r: number, leaf: StandardMaterial): Mesh => {
      const trunk = MeshBuilder.CreateCylinder(name + ":trunk", { height: h * 0.35, diameter: 0.5 }, scene);
      trunk.position.y = h * 0.175;
      trunk.material = bark;
      const crown = MeshBuilder.CreateCylinder(name + ":crown", { height: h * 0.75, diameterTop: 0, diameterBottom: r * 2, tessellation: 7 }, scene);
      crown.position.y = h * 0.35 + h * 0.375;
      crown.material = leaf;
      const merged = Mesh.MergeMeshes([trunk, crown], true, true, undefined, false, true)!;
      merged.name = name;
      merged.isVisible = false;
      merged.isPickable = false;
      return merged;
    };
    this.treeA = leaves.map((l, i) => mkTree(`treeA${i}`, 7, 2.2, l)) as TreeSet;
    this.treeB = leaves.map((l, i) => mkTree(`treeB${i}`, 10, 2.8, l)) as TreeSet;
    this.rock = MeshBuilder.CreateIcoSphere("rock", { radius: 1, subdivisions: 1, flat: true }, scene);
    this.rock.scaling.y = 0.6;
    this.rock.material = stone;
    this.rock.isVisible = false;
    this.rock.isPickable = false;

    const plots = cfg.plots.map((p) => p.bounds);
    const ex = cfg.scenery.exclusionRadius;
    this.exclusion = (x, z) =>
      Math.hypot(x, z) < ex ||
      plots.some((b) => x >= b.min[0] - 4 && x < b.max[0] + 4 && z >= b.min[1] - 4 && z < b.max[1] + 4) ||
      cfg.landmarks.some((l) => Math.hypot(x - l.pos[0], z - l.pos[1]) < (l.radius ?? Math.max(...(l.size ?? [20]))) + 6);
  }

  onChunk(cx: number, cz: number): void {
    const key = `${cx}:${cz}`;
    if (this.instances.has(key)) return;
    const list: InstancedMesh[] = [];
    const place = (src: Mesh | TreeSet, pts: { x: number; z: number; r: number }[], scale: [number, number]) => {
      for (const p of pts) {
        const mesh = Array.isArray(src) ? src[leafIndex(p.r)] : src;
        const wx = cx * CHUNK + p.x, wz = cz * CHUNK + p.z;
        if (this.exclusion(wx, wz)) continue;
        const inst = mesh.createInstance(`${mesh.name}:${key}:${list.length}`);
        inst.position.set(wx, this.height(wx, wz), wz);
        inst.rotation.y = p.r * Math.PI * 2;
        const s = scale[0] + (scale[1] - scale[0]) * p.r;
        inst.scaling.set(s, s, s);
        inst.isPickable = false;
        inst.freezeWorldMatrix();
        list.push(inst);
      }
    };
    const trees = scatterPoints(cx, cz, this.cfg.scenery.treeDensity, this.cfg.terrain.seed, 5);
    place(this.treeA, trees.filter((p) => p.r < 0.6), [0.8, 1.2]);
    place(this.treeB, trees.filter((p) => p.r >= 0.6), [0.8, 1.2]);
    place(this.rock, scatterPoints(cx, cz, this.cfg.scenery.rockDensity, this.cfg.terrain.seed + 1, 8), [0.6, 1.8]);
    this.instances.set(key, list);
  }

  offChunk(cx: number, cz: number): void {
    const key = `${cx}:${cz}`;
    for (const i of this.instances.get(key) ?? []) i.dispose();
    this.instances.delete(key);
  }
}
